import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Building, 
  Wrench, 
  Droplet, 
  Zap, 
  Shield, 
  Star,
  Phone,
  CheckCircle,
  ArrowRight,
  Play,
  Image as ImageIcon,
  Camera,
  FileText,
  MapPin,
  Users,
  AlertCircle
} from 'lucide-react';

const CommercialPlumbing = () => {
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/#contact');
    // Small delay to ensure navigation happens first
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  useEffect(() => {
    // Meta tags are now handled by static generation in generate-static.cjs
    // This ensures consistency and avoids conflicts between React and static HTML

    // Add Commercial Plumbing Service Schema for AI SEO
    const commercialServiceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://colorado-plumbing.com/commercial-plumbing#service',
      'name': 'Commercial Plumbing Services',
      'description': 'Expert commercial plumbing services in Denver Metro specializing in HOA communities, apartment complexes, office buildings, and commercial properties. Backflow testing, roof drain cleaning, and emergency services.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'email': 'codee@colorado-plumbing.com',
        'url': 'https://colorado-plumbing.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Highlands Ranch',
          'addressRegion': 'CO',
          'addressCountry': 'US'
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '360',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      'areaServed': [
        {
          '@type': 'City',
          'name': 'Denver',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Highlands Ranch',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Littleton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Lakewood',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Arvada',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Thornton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Longmont',
          'addressRegion': 'CO'
        }
      ],
      'serviceType': 'Commercial Plumbing',
      'category': 'Plumbing Services',
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Commercial Plumbing Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'HOA Plumbing Services',
              'description': 'Specialized plumbing services for homeowner associations and multi-unit properties'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Backflow Testing & Certification',
              'description': 'Annual backflow testing and certification services for commercial properties'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Roof Drain Cleaning',
              'description': 'Professional roof drain cleaning and maintenance for commercial buildings'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Commercial Emergency Services',
              'description': '24/7 emergency plumbing response for commercial and multi-unit properties'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Commercial Fixture Installation',
              'description': 'Installation and repair of commercial-grade plumbing fixtures and equipment'
            }
          }
        ]
      },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'USD',
        'availability': 'https://schema.org/InStock',
        'description': 'Professional commercial plumbing services with HOA specialization and emergency availability'
      },
      'additionalProperty': [
        {
          '@type': 'PropertyValue',
          'name': 'Emergency Service',
          'value': '24/7 Available'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Master Plumber Owned',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'HOA Specialist',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Backflow Certified',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Property Management Partner',
          'value': 'Multiple Companies'
        }
      ]
    };

    // Inject the service schema
    let serviceSchemaScript = document.querySelector('#commercial-service-schema');
    if (serviceSchemaScript) {
      serviceSchemaScript.textContent = JSON.stringify(commercialServiceSchema);
    } else {
      serviceSchemaScript = document.createElement('script');
      serviceSchemaScript.setAttribute('id', 'commercial-service-schema');
      serviceSchemaScript.setAttribute('type', 'application/ld+json');
      serviceSchemaScript.textContent = JSON.stringify(commercialServiceSchema);
      document.head.appendChild(serviceSchemaScript);
    }
  }, []);

  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    // Load the BirdEye widget after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      loadBirdEyeWidget();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      <main>
        <section className="pt-56 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] bg-repeat"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center pt-8 pb-8">
              <div className="flex items-center justify-center mb-6">
                <Building className="mr-3 text-action-warm" size={40} />
                <span className="text-sm font-medium bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full border border-white/30">
                  Commercial & HOA Specialists
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-neutral-white">
                Denver's Premier Commercial Plumbing & HOA Experts
              </h1>

              <p className="text-xl mb-8 text-trust-light/90">
                Professional commercial plumbing for businesses, HOAs, and commercial properties. Specialized expertise in <Link to="/sewer-repair-replacement" className="text-action-warm hover:text-white underline">sewer services</Link>, 
                backflow testing, and comprehensive project documentation. Colorado's only master plumber owned company with <Link to="/pricing" className="text-action-warm hover:text-white underline">transparent pricing</Link> and preventative maintenance focus.
              </p>
              
              <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
                <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
                <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                  <p>Reviews loading... See our 300+ verified commercial project reviews on Google and BirdEye.</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <button 
                  onClick={handleContactClick}
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  Get Free Commercial Estimate
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Colorado-Specific Expertise Section - Flipped gradient */}
        <section className="py-16 md:py-20 bg-gradient-to-bl from-action-primary via-authority-navy to-trust-primary text-white relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Colorado Front Range Plumbing Expertise</h2>
              <p className="text-lg text-trust-light/90">
                Colorado's unique environment presents specific challenges that require specialized knowledge. Our team understands 
                these complexities and provides expert solutions tailored to Front Range conditions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {/* Harsh Winters */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-blue-500/20 w-fit mx-auto mb-4">
                  <AlertCircle className="text-blue-300" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Harsh Winter Protection</h3>
                <p className="text-sm text-trust-light/80">
                  Expert freeze-thaw cycle management, pipe insulation, and winterization services to prevent costly burst pipes and water damage.
                </p>
              </div>

              {/* Expansive Soils */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-orange-500/20 w-fit mx-auto mb-4">
                  <MapPin className="text-orange-300" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Expansive Soil Solutions</h3>
                <p className="text-sm text-trust-light/80">
                  Understanding of Colorado's swelling soils and their impact on foundations and underground pipes. Proactive leak detection and drainage solutions.
                </p>
              </div>

              {/* Water Quality */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-green-500/20 w-fit mx-auto mb-4">
                  <Droplet className="text-green-300" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Hard Water Expertise</h3>
                <p className="text-sm text-trust-light/80">
                  Denver area water hardness management, scale prevention, and water quality solutions to protect your commercial plumbing systems.
                </p>
              </div>

              {/* High Altitude */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-purple-500/20 w-fit mx-auto mb-4">
                  <Zap className="text-purple-300" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">High Altitude Dynamics</h3>
                <p className="text-sm text-trust-light/80">
                  Specialized knowledge of pressure dynamics at altitude to ensure optimal water pressure and system performance in commercial properties.
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg max-w-4xl mx-auto border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white text-center">Why Colorado Expertise Matters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2 text-action-warm">Environmental Challenges</h4>
                  <ul className="space-y-1 text-sm text-trust-light/80">
                    <li>• Freeze-thaw cycles causing pipe expansion and contraction</li>
                    <li>• Soil movement affecting underground infrastructure</li>
                    <li>• Mineral buildup from hard water reducing system efficiency</li>
                    <li>• Pressure variations due to elevation changes</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-action-warm">Our Specialized Solutions</h4>
                  <ul className="space-y-1 text-sm text-trust-light/80">
                    <li>• Comprehensive winterization and insulation services</li>
                    <li>• Advanced leak detection using thermal imaging</li>
                    <li>• Water softening system recommendations and installation</li>
                    <li>• Pressure optimization for high-altitude properties</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary text-white relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Complete Commercial Plumbing Services</h2>
              <p className="text-lg text-blue-100">
                From preventative maintenance to complex HOA projects, our experienced team handles every aspect of commercial plumbing 
                with extensive documentation and professional expertise. We understand HOA responsibilities vs. homeowner responsibilities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* HOA & Multi-Unit Services */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-blue-50 w-fit mb-4">
                  <Users className="text-blue-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3">HOA & Multi-Unit Specialists</h3>
                <p className="text-gray-600 mb-4">Expert HOA plumbing services with deep knowledge of responsibility boundaries and community needs. Serving <Link to="/highlands-ranch-plumber" className="text-trust-primary underline hover:text-action-primary">Highlands Ranch</Link>, <Link to="/littleton-plumber" className="text-trust-primary underline hover:text-action-primary">Littleton</Link>, and all Denver Metro communities.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">HOA vs. Homeowner Responsibility Expertise</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Multi-Unit Building Plumbing</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Community Common Area Services</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">
                      <Link to="/sewer-repair-replacement" className="text-blue-600 hover:text-blue-800">Main Line Sewer Services</Link>
                    </span>
                  </li>
                </ul>
              </div>
              {/* Backflow & Roof Drains */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-blue-50 w-fit mb-4">
                  <Droplet className="text-blue-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3">Backflow & Roof Drain Services</h3>
                <p className="text-gray-600 mb-4">Specialized commercial services including backflow prevention and roof drainage systems. Professional services in <Link to="/denver-plumber" className="text-trust-primary underline hover:text-action-primary">Denver</Link>, <Link to="/lakewood-plumber" className="text-trust-primary underline hover:text-action-primary">Lakewood</Link>, and surrounding areas.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Backflow Replacement & Testing</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Roof Drain Cleaning & Repair</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Commercial Drain Line Services</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Storm Water Management</span>
                  </li>
                </ul>
              </div>

              {/* Commercial Systems */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-blue-50 w-fit mb-4">
                  <Building className="text-blue-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3">Commercial Plumbing Systems</h3>
                <p className="text-gray-600 mb-4">Complete commercial building plumbing services for businesses and commercial properties. Expert <Link to="/water-heater-pricing" className="text-trust-primary underline hover:text-action-primary">water heater services</Link> and commercial fixture installations throughout Denver Metro.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Commercial Water Heater Systems</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Business Property Fixture Services</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Commercial Gas Line Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-400 mr-2" size={16} />
                    <span className="text-sm text-trust-light/80">Store & Office Plumbing Maintenance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Specialized Client Segments - Flipped gradient */}
        <section className="py-16 md:py-20 bg-gradient-to-bl from-action-primary via-authority-navy to-trust-primary text-white relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Specialized Solutions for Every Client Type</h2>
              <p className="text-lg text-trust-light/90">
                We understand that different commercial clients have unique needs, challenges, and priorities. Our specialized approach 
                ensures you get exactly the right solution for your specific situation.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* HOA Segment */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-blue-500/20 w-fit mb-6">
                  <Users className="text-blue-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Homeowners Associations</h3>
                <p className="text-trust-light/80 mb-6">
                  Managing multi-unit residential properties with shared plumbing systems and complex liability issues requires specialized expertise.
                </p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-action-warm">Common HOA Challenges We Solve:</h4>
                  <ul className="space-y-2 text-sm text-trust-light/80">
                    <li>• Shared drain line blockages causing widespread backups</li>
                    <li>• Disputes over responsibility boundaries for water damage</li>
                    <li>• Ambiguity in governing documents (CC&Rs) regarding maintenance</li>
                    <li>• Main line issues affecting multiple units simultaneously</li>
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <h4 className="font-semibold mb-2 text-white">Our HOA Solutions:</h4>
                  <ul className="space-y-1 text-sm text-trust-light/80">
                    <li>✓ <Link to="/sewer-repair-replacement" className="text-action-warm hover:text-white">Main line cleaning and sewer services</Link></li>
                    <li>✓ Common area repairs with clear documentation</li>
                    <li>✓ Backflow prevention and annual testing</li>
                    <li>✓ Preventative maintenance plans for long-term savings</li>
                    <li>✓ Clear responsibility boundary expertise</li>
                  </ul>
                </div>
              </div>

              {/* Property Management Segment */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-green-500/20 w-fit mb-6">
                  <Building className="text-green-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Property Management Companies</h3>
                <p className="text-trust-light/80 mb-6">
                  Overseeing rental properties requires prioritizing tenant satisfaction, property preservation, and cost-efficiency.
                </p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-action-warm">Common Property Management Issues:</h4>
                  <ul className="space-y-2 text-sm text-trust-light/80">
                    <li>• Water heater failures disrupting tenant comfort</li>
                    <li>• Main drain blockages affecting multiple units</li>
                    <li>• Hidden leaks leading to expensive damage</li>
                    <li>• Burst pipes during Colorado winters</li>
                    <li>• Tenant-related drain clogs and running toilets</li>
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <h4 className="font-semibold mb-2 text-white">Our Property Management Solutions:</h4>
                  <ul className="space-y-1 text-sm text-trust-light/80">
                    <li>✓ <Link to="/water-heater-pricing" className="text-action-warm hover:text-white">Prompt water heater repairs and replacements</Link></li>
                    <li>✓ Routine inspections and maintenance schedules</li>
                    <li>✓ Rapid resolution of tenant-related issues</li>
                    <li>✓ <Link to="/pricing" className="text-action-warm hover:text-white">Cost-effective solutions with transparent pricing</Link></li>
                    <li>✓ Detailed documentation for insurance claims</li>
                  </ul>
                </div>
              </div>

              {/* General Commercial Segment */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-orange-500/20 w-fit mb-6">
                  <Wrench className="text-orange-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">General Commercial Businesses</h3>
                <p className="text-trust-light/80 mb-6">
                  A wide array of property types with unique operational demands requiring specialized commercial expertise.
                </p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-action-warm">Commercial Business Challenges:</h4>
                  <ul className="space-y-2 text-sm text-trust-light/80">
                    <li>• Grease trap issues in restaurants and food service</li>
                    <li>• Complex plumbing needs in healthcare facilities</li>
                    <li>• High-rise building specialized requirements</li>
                    <li>• Disruptions to daily operations from plumbing failures</li>
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <h4 className="font-semibold mb-2 text-white">Our Commercial Business Solutions:</h4>
                  <ul className="space-y-1 text-sm text-trust-light/80">
                    <li>✓ Comprehensive service range from installation to maintenance</li>
                    <li>✓ Specialized services like grease trap cleaning</li>
                    <li>✓ Water quality testing and treatment</li>
                    <li>✓ Minimized disruption with efficient scheduling</li>
                    <li>✓ Strict compliance with UPC/IPC codes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Common Issues & Our Solutions */}
        <section className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Common Commercial Plumbing Issues & Our Expert Solutions</h2>
              <p className="text-lg text-blue-100">
                Understanding the root causes of plumbing problems allows us to provide lasting solutions rather than temporary fixes. 
                Here's how we address the most common commercial plumbing challenges in Colorado.
              </p>
            </div>

            <div className="space-y-8">
              {/* Drain Cleaning & Blockages */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-blue-100 mr-3">
                        <Droplet className="text-blue-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Drain Cleaning & Blockages</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Common Causes:</strong> Accumulation of debris, grease, and hair. Outdoor drains especially prone to clogging during Colorado's monsoon season.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-action-warm">Our Solution Approach:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• <Link to="/hydro-jetting-pricing" className="text-blue-600 hover:text-blue-800">Professional hydro jetting services</Link></li>
                        <li>• Regular preventative cleaning schedules</li>
                        <li>• Installation of drain screens and guards</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• Video camera inspections to identify damage</li>
                        <li>• Detection of pipe "bellies" causing recurring issues</li>
                        <li>• Long-term maintenance planning</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Water Heater Services */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-orange-100 mr-3">
                        <Zap className="text-orange-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Water Heater Services</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Common Issues:</strong> Insufficient hot water, leaks, sediment buildup, poor water quality, and loud noises affecting commercial operations.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-action-warm">Our Solution Approach:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• <Link to="/water-heater-pricing" className="text-orange-600 hover:text-orange-800">Installation and repair of tank/tankless heaters</Link></li>
                        <li>• Regular flushing to remove sediment buildup</li>
                        <li>• Energy-efficient replacement recommendations</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• <Link to="/tankless-water-heater-pricing" className="text-orange-600 hover:text-orange-800">Commercial tankless system upgrades</Link></li>
                        <li>• Preventative maintenance programs</li>
                        <li>• Hard water impact mitigation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sewer Line Services */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-green-100 mr-3">
                        <Shield className="text-green-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Sewer Line Services</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Common Issues:</strong> Tree root infiltration, recurring clogs, and structural issues in aging sewer infrastructure.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-action-warm">Our Solution Approach:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• <Link to="/sewer-repair-replacement" className="text-green-600 hover:text-green-800">Advanced video camera inspections</Link></li>
                        <li>• Professional hydro jetting for root removal</li>
                        <li>• Trenchless sewer repair to minimize disruption</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• Comprehensive documentation with video evidence</li>
                        <li>• Root barrier installation for prevention</li>
                        <li>• Long-term monitoring and maintenance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Backflow Prevention */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-red-100 mr-3">
                        <AlertCircle className="text-red-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Backflow Prevention</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Critical Issue:</strong> Polluted water flowing back into clean supply poses serious health risks and legal consequences for commercial properties.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-action-warm">Our Solution Approach:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• Installation of certified backflow prevention devices</li>
                        <li>• Annual testing by licensed personnel</li>
                        <li>• Compliance documentation for health departments</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-trust-light/80">
                        <li>• Risk assessment and system design</li>
                        <li>• Regular maintenance and monitoring</li>
                        <li>• Response planning for backflow incidents</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg max-w-2xl mx-auto border border-white/20">
                <h3 className="text-xl font-bold mb-3 text-white">Proactive vs. Reactive Approach</h3>
                <p className="text-blue-100 mb-4">
                  While many companies focus on reactive repairs, we emphasize preventative maintenance and root cause solutions. 
                  This approach saves money, prevents costly disruptions, and extends system life.
                </p>
                <Link 
                  to="/pricing"
                  className="inline-flex items-center px-6 py-3 bg-trust-primary text-white rounded-lg font-semibold hover:bg-authority-navy transition-colors"
                >
                  See Our Maintenance Plans
                  <ArrowRight className="ml-2" size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Competitive Advantages - Flipped gradient */}
        <section className="py-20 bg-gradient-to-bl from-action-primary via-authority-navy to-trust-primary text-white relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Why Choose Colorado First Plumbing for Commercial Projects?</h2>
              <p className="text-lg text-trust-light/90">
                While the market is saturated with "full-service" providers focused on reactive repairs, we differentiate ourselves 
                through specialized expertise, proactive solutions, and deep understanding of Colorado's unique challenges.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Colorado-Specific Expertise */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-blue-500/20 w-fit mb-4">
                  <MapPin className="text-blue-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Colorado Front Range Expertise</h3>
                <p className="text-trust-light/80 mb-4">
                  Our specialized knowledge of Colorado's environment sets us apart. We understand expansive soils, harsh winters, 
                  water hardness, and high-altitude effects on plumbing systems. This expertise prevents recurring problems and 
                  saves money through proper solutions.
                </p>
                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-400/20">
                  <p className="text-blue-200 font-semibold">✓ Expansive soil impact expertise</p>
                  <p className="text-blue-800 font-semibold">✓ Freeze-thaw cycle management</p>
                  <p className="text-blue-800 font-semibold">✓ Hard water system protection</p>
                  <p className="text-blue-800 font-semibold">✓ High-altitude pressure optimization</p>
                </div>
              </div>

              {/* Regulatory Compliance & Risk Mitigation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-green-500/20 w-fit mb-4">
                  <Shield className="text-green-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Regulatory Compliance Excellence</h3>
                <p className="text-trust-light/80 mb-4">
                  Strict adherence to Colorado plumbing codes (IPC, IRC, IFGC) and proper licensing provide strategic risk mitigation 
                  for commercial clients. Our compliance expertise protects HOAs and property managers from liability issues.
                </p>
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-400/20">
                  <p className="text-green-200 font-semibold">✓ Master plumber owned and operated</p>
                  <p className="text-green-800 font-semibold">✓ Full IPC/IRC/IFGC code compliance</p>
                  <p className="text-green-800 font-semibold">✓ Certified backflow prevention specialist</p>
                  <p className="text-green-800 font-semibold">✓ Complete liability protection</p>
                </div>
              </div>

              {/* Advanced Diagnostic Capabilities */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-orange-500/20 w-fit mb-4">
                  <Camera className="text-orange-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Advanced Diagnostic Technology</h3>
                <p className="text-trust-light/80 mb-4">
                  Investment in cutting-edge diagnostic tools allows us to identify root causes rather than treating symptoms. 
                  Our comprehensive documentation provides value for insurance claims and long-term planning.
                </p>
                <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-400/20">
                  <p className="text-orange-200 font-semibold">✓ Thermal imaging leak detection</p>
                  <p className="text-orange-800 font-semibold">✓ Advanced video camera inspections</p>
                  <p className="text-orange-800 font-semibold">✓ Professional locate services</p>
                  <p className="text-orange-800 font-semibold">✓ Comprehensive project documentation</p>
                </div>
              </div>

              {/* Preventative Maintenance Focus */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-purple-500/20 w-fit mb-4">
                  <Wrench className="text-purple-300" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Preventative Maintenance Leadership</h3>
                <p className="text-trust-light/80 mb-4">
                  While competitors focus on reactive emergency services, we prioritize preventative solutions that save money 
                  and prevent disruptions. Our maintenance plans foster long-term partnerships with stable pricing.
                </p>
                <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-400/20">
                  <p className="text-purple-200 font-semibold">✓ Proactive maintenance programs</p>
                  <p className="text-purple-200 font-semibold">✓ Root cause problem solving</p>
                  <p className="text-purple-200 font-semibold">✓ Long-term partnership approach</p>
                  <p className="text-purple-200 font-semibold">✓ <Link to="/pricing" className="underline hover:text-white">Transparent pricing with no surprises</Link></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Work Documentation Gallery */}
        <section className="py-20 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary text-white relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Our Commercial Work Documentation</h2>
              <p className="text-lg text-blue-100">
                See examples of our comprehensive commercial and HOA plumbing projects with full documentation across Denver Metro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Commercial Hanging Pipe Installation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/20">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/commercial-plumbing/commercial-hanging-pipe-multifamily-residential.jpg" 
                    alt="Commercial Hanging Pipe Installation"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Project Gallery
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Commercial Pipe Installation</h3>
                  <p className="text-blue-100 text-sm">Multi-family residential and commercial pipe hanging with professional installation</p>
                </div>
              </div>

              {/* Commercial Water Heater Installation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/20">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/commercial-plumbing/commercial-water-heater-install-denver.JPEG" 
                    alt="Commercial Water Heater Installation Denver"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Installation Project
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Commercial Water Heater Installation</h3>
                  <p className="text-blue-100 text-sm">Professional commercial water heater installation in Denver with complete documentation</p>
                </div>
              </div>

              {/* Roof Drain Cleaning Video */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/20">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/commercial-plumbing/roof-drain-cleaning-denver.JPEG"
                    alt="Commercial Roof Drain Cleaning Denver"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Roof Drain Service
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Commercial Roof Drain Cleaning</h3>
                  <p className="text-blue-100 text-sm">Professional roof drain cleaning and testing documentation for commercial properties</p>
                </div>
              </div>

              {/* Commercial Sewer Cleaning */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/20">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/commercial-plumbing/commercial-sewer-cleaning-lakewood.JPEG" 
                    alt="Commercial Sewer Cleaning Lakewood"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Sewer Service
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Commercial Sewer Line Cleaning</h3>
                  <p className="text-blue-100 text-sm">Professional commercial sewer cleaning in Lakewood with advanced equipment</p>
                </div>
              </div>

              {/* Thermal Camera Leak Detection */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/20">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/commercial-plumbing/thermal-camera-leak-detection-denver.JPEG" 
                    alt="Thermal Camera Leak Detection Denver"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Advanced Technology
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Thermal Camera Leak Detection</h3>
                  <p className="text-blue-100 text-sm">Advanced thermal camera technology for precise leak detection in commercial properties</p>
                </div>
              </div>

              {/* Commercial Plumbing Contractor */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-white/20">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/commercial-plumbing/commercial-plumbing-contractor-castle-rock.JPEG" 
                    alt="Commercial Plumbing Contractor Castle Rock"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-indigo-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Professional Service
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing Projects</h3>
                  <p className="text-blue-100 text-sm">Professional commercial plumbing services in Castle Rock and throughout Denver Metro</p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-blue-100 mb-6">
                Every project includes comprehensive documentation, video evidence, and detailed reporting for HOAs and property managers.
              </p>
              <Link 
                to="/reviews"
                className="inline-flex items-center px-8 py-4 bg-trust-primary text-white rounded-lg font-semibold hover:bg-authority-navy transition-colors"
              >
                View All Project Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>
        {/* Customer Reviews Section */}
        <section className="py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="flex items-center justify-center mb-4">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-action-warm fill-action-warm" size={24} />
                  ))}
                </div>
                <span className="ml-3 text-2xl font-bold text-white">4.9/5 (310+ Reviews)</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Commercial Clients Love Our Professional Service</h2>
              <p className="text-lg text-blue-100">
                See what our commercial customers, HOAs, and business owners have to say about our expert plumbing services.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Review 1 - Professional Trust (Real HomeAdvisor Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-blue-400">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">HomeAdvisor</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "They earned my full trust and only wish I could have taken them with me when I moved out if the state of Colorado. I have zero hesitation in recommending this company. They might not be the cheapest but they are the best. They did give me a disabled veterans discount. Do yourself a favor, if you need a plumber, hire this company."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Timothy B.</p>
                  <p className="text-gray-600">Colorado Customer</p>
                </div>
              </div>
              {/* Review 2 - Big Projects (Real BirdEye Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-green-400">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">BirdEye</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "Codee and his team at Colorado First Plumbing are the best. We've used them for several years on big projects and small ones, and they always deliver for a very reasonable price. We cannot speak more highly of this company."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Commercial Customer</p>
                  <p className="text-gray-600">Multi-Year Client</p>
                </div>
              </div>

              {/* Review 3 - Professional Service (Real Angi Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-purple-400">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">Angi</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "Nick from Colorado First Plumbing and Sewer did an amazing job on a stoppage in my kitchen sink! He was very professional and personable. He even gave me tips to keep the sink running smoothly. I mentioned an issue with a toilet running so he took a look and fixed in no time! I would HIGHLY recommend them for your plumbing needs!"
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Satisfied Customer</p>
                  <p className="text-gray-600">Perfect Service</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link 
                to="/reviews"
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                View All 293+ Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>
        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-trust-primary via-authority-navy to-action-primary text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] bg-repeat"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Professional Commercial Plumbing Excellence?</h2>
              <p className="text-xl mb-8 text-trust-light/90">
                From preventative maintenance to complex HOA projects, we're Denver's most trusted commercial plumbing experts. 
                Experience Colorado-specific expertise with our <Link to="/pricing" className="text-action-warm hover:text-white underline">transparent pricing</Link> and comprehensive documentation approach.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                
                <button 
                  onClick={handleContactClick}
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  Schedule Commercial Estimate
                </button>
              </div>
              
              <div className="text-center">
                <p className="text-trust-light/80 text-sm">
                  ✓ Master Plumber Owned  ✓ 4.9★ Rating  ✓ Colorado Expertise  ✓ HOA Specialists  ✓ Regulatory Compliance  ✓ <Link to="/pricing" className="text-action-warm hover:text-white underline">Transparent Pricing</Link>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default CommercialPlumbing;